import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useCurrentProject } from "@/features/projects/current-project";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { type UploadStartParams, useConfirmUpload, useStartUpload } from "./mutations";

function inferFormat(filename: string): UploadStartParams["format"] | null {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (ext === "csv") return "csv";
  if (ext === "parquet" || ext === "pq") return "parquet";
  return null;
}

export function UploadDatasetDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const { projectId } = useCurrentProject();

  const start = useStartUpload();
  const confirm = useConfirmUpload();

  const reset = () => {
    setName("");
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!file || !name) return;
    if (!projectId) {
      toast.error("Select a project first");
      return;
    }
    const format = inferFormat(file.name);
    if (!format) {
      toast.error("Unsupported format — only CSV and Parquet are accepted");
      return;
    }
    setBusy(true);
    let started: { upload_url: string; upload_token: string };
    try {
      started = await start.mutateAsync({
        name,
        size_bytes: file.size,
        format,
        project_id: projectId,
      });
      const putResp = await fetch(started.upload_url, {
        method: "PUT",
        body: file,
        headers: { "content-type": file.type || "application/octet-stream" },
      });
      if (!putResp.ok) throw new Error(`Storage upload failed: ${putResp.statusText}`);
    } catch (e) {
      toast.error(`Upload failed: ${(e as Error).message}`);
      setBusy(false);
      return;
    }
    try {
      await confirm.mutateAsync(started.upload_token);
      onOpenChange(false);
      reset();
    } catch {
      // onError already toasted
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent>
        <DialogTitle>Upload dataset</DialogTitle>
        <DialogDescription>
          Select a CSV or Parquet file. It will be uploaded directly to storage and profiled
          automatically.
        </DialogDescription>
        <div className="mt-4 space-y-3">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: Input is nested inside label */}
          <label className="block">
            <span className="text-xs text-muted">Dataset name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="retail_q3_raw"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">File</span>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.parquet,.pq"
              className="mt-1 block w-full text-xs text-muted file:mr-2 file:rounded file:border file:border-line file:bg-surface file:px-2 file:py-1 file:text-xs"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <DialogClose asChild>
            <Button variant="default" disabled={busy}>
              Cancel
            </Button>
          </DialogClose>
          <Button variant="primary" disabled={!name || !file || busy} onClick={submit}>
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
